import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Play } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CurrencySelect } from '../CurrencySelect'
import { DateRangeSelect } from '../DateRangeSelect'
import { FieldLabel } from '../FieldLabel'
import { ObjectPicker } from '../ObjectPicker'
import { SearchSelect } from '../SearchSelect'
import { TokenInput } from '../TokenInput'
import { ReportObjectSelect } from './ReportObjectSelect'
import { useNetwork } from '../../contexts/NetworkContext'
import { useCurrentUser } from '../../contexts/UserContext'
import { SearchApi } from '@/services/core/searchApi'
import { ReportsApi, type ReportExecution } from '@/services/reports/reportsApi'
import { ReportTemplatesApi, type ReportRef } from '@/services/reports/reportTemplatesApi'
import {
  REPORT_TYPES,
  hasChildTemplate,
  reportTypesFor,
  type ReportType,
} from '@/services/reports/reportTypes'
import { dateRangeLabel, getDateRange, type DateRange } from '@/services/stats/statsRange'
import { formatDate } from '@/utils/format'

interface RequestReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the report once the engine has accepted it. */
  onRequested: (execution: ReportExecution) => void
}

/** An order report can cover the whole order or a hand-picked set of its line items. */
type ListSelection = 'ORDER' | 'LINE_ITEMS'

/**
 * Runs one report against any object in the network: pick what kind of thing
 * to report on, which one, and which template to lay it out with. Reports on
 * campaigns cover the campaign's own flight unless dates are given; reports on
 * inventory always need a period.
 */
export function RequestReportDialog({ open, onOpenChange, onRequested }: RequestReportDialogProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const reportTypes = reportTypesFor(currentNetwork)

  const [reportType, setReportType] = useState<ReportType>('LINE_ITEM')
  const [object, setObject] = useState<ReportRef | null>(null)
  const [listSelection, setListSelection] = useState<ListSelection>('ORDER')
  const [lineItems, setLineItems] = useState<ReportRef[]>([])
  const [templateId, setTemplateId] = useState('')
  const [childTemplateId, setChildTemplateId] = useState('')
  const [specifyDates, setSpecifyDates] = useState(false)
  const [range, setRange] = useState<DateRange>('30d')
  const [currency, setCurrency] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [name, setName] = useState('')
  const [nameEdited, setNameEdited] = useState(false)

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['report-templates', 'all'],
    queryFn: () => ReportTemplatesApi.getAll(),
    enabled: open,
    staleTime: 60 * 1000,
  })

  const definition = REPORT_TYPES[reportType]
  // A line item can also be reported on with a self-service template.
  const typeTemplates = templates.filter(
    (tpl) =>
      tpl.reportType === reportType || (reportType === 'LINE_ITEM' && tpl.reportType === 'SELF_SERVICE')
  )
  const childTemplates = templates.filter((tpl) => tpl.reportType === 'LINE_ITEM')
  // The first template that fits the chosen type, until another is picked.
  const template = typeTemplates.find((tpl) => tpl.id === templateId) ?? typeTemplates[0]
  const effectiveTemplateId = template?.id ?? ''
  const datesRequired = !definition.hasFlight
  const usesDates = datesRequired || specifyDates
  const pickingLineItems = reportType === 'LINE_ITEM_LIST' && listSelection === 'LINE_ITEMS'
  const availableCurrencies = currentNetwork?.availableCurrencies ?? []

  // Each opening starts from the defaults rather than the last report's choices.
  useEffect(() => {
    if (!open) return
    setObject(null)
    setLineItems([])
    setListSelection('ORDER')
    setTemplateId('')
    setChildTemplateId('')
    setSpecifyDates(false)
    setRange('30d')
    setCurrency(currentUser?.preferredCurrency || currentNetwork?.defaultCurrency || '')
    setEmails([])
    setNameEdited(false)
  }, [open, currentUser?.preferredCurrency, currentNetwork?.defaultCurrency])

  const objectName =
    reportType === 'NETWORK'
      ? currentNetwork?.name
      : pickingLineItems
        ? lineItems.map((li) => li.name).join(', ')
        : object?.name
  const defaultName = useMemo(
    () =>
      [template?.name, objectName, formatDate(new Date().toISOString())].filter(Boolean).join(' — '),
    [template?.name, objectName]
  )
  useEffect(() => {
    if (!nameEdited) setName(defaultName)
  }, [defaultName, nameEdited])

  const changeType = (type: ReportType) => {
    setReportType(type)
    setObject(null)
    setLineItems([])
    setChildTemplateId('')
  }

  const hasTarget =
    reportType === 'NETWORK' || (pickingLineItems ? lineItems.length > 0 : !!object)
  const valid = hasTarget && !!effectiveTemplateId && !!name.trim()

  const mutation = useMutation({
    mutationFn: () => {
      const parameters: Record<string, unknown> = {
        localePriority: 'descending',
        timeZone,
        currency: currency || currentNetwork?.defaultCurrency || 'EUR',
        locale: currentUser?.locale || 'en_GB',
      }
      if (usesDates) Object.assign(parameters, getDateRange(range, timeZone))
      if (reportType === 'NETWORK') parameters[definition.idKey] = currentNetwork?.id
      else if (pickingLineItems) parameters.lineItemIds = lineItems.map((li) => li.id)
      else parameters[definition.idKey] = object!.id

      return ReportsApi.run({
        name: name.trim(),
        templateId: effectiveTemplateId,
        childTemplateId: hasChildTemplate(reportType) ? childTemplateId || undefined : undefined,
        parameters,
        relatedLineItemId: reportType === 'LINE_ITEM' ? object?.id : undefined,
        recipientEmailAddresses: emails,
      })
    },
    onSuccess: (execution) => {
      toast.success(t('reports.queued'))
      onRequested(execution)
      onOpenChange(false)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const searchLineItems = async (term: string) =>
    (await SearchApi.search({ q: term, types: 'LineItem', pageSize: 50 })).searchResults.map(
      (r) => ({ id: r.id, name: r.name })
    )

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('reportsModule.requestTitle')}</DialogTitle>
          <DialogDescription>{t('reportsModule.requestHelp')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="p-field">
            <FieldLabel hint={t(`reportTypes.${reportType}.explanation`)}>
              {t('reportsModule.reportType')}
            </FieldLabel>
            <Select value={reportType} onValueChange={(v) => changeType(v as ReportType)}>
              <SelectTrigger>
                <SelectValue>{t(`reportTypes.${reportType}.simple`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {reportTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`reportTypes.${type}.simple`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reportType === 'LINE_ITEM_LIST' && (
            <RadioGroup
              value={listSelection}
              onValueChange={(v) => setListSelection(v as ListSelection)}
              className="flex flex-wrap gap-4"
            >
              <Label className="font-normal">
                <RadioGroupItem value="ORDER" />
                {t('reportsModule.wholeOrder')}
              </Label>
              <Label className="font-normal">
                <RadioGroupItem value="LINE_ITEMS" />
                {t('reportsModule.someLineItems')}
              </Label>
            </RadioGroup>
          )}

          {pickingLineItems ? (
            <div className="p-field">
              <FieldLabel>{t('reportsModule.lineItems')}</FieldLabel>
              <ObjectPicker
                queryKey="report-line-items"
                search={searchLineItems}
                selected={lineItems}
                onChange={setLineItems}
                addLabel={t('reportsModule.addLineItems')}
                emptyText={t('reportsModule.noLineItems')}
              />
            </div>
          ) : (
            definition.searchType && (
              <div className="p-field">
                <FieldLabel>{t(`reportTypes.${reportType}.object`)}</FieldLabel>
                <ReportObjectSelect
                  key={reportType}
                  searchType={definition.searchType}
                  value={object}
                  onChange={setObject}
                  placeholder={t('reportsModule.selectObject')}
                />
              </div>
            )
          )}

          <div className="p-field">
            <FieldLabel hint={template?.description || undefined}>{t('reports.template')}</FieldLabel>
            <SearchSelect
              value={effectiveTemplateId}
              onChange={setTemplateId}
              options={typeTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
              placeholder={t('reports.templatePlaceholder')}
              loading={templatesLoading}
            />
            {!templatesLoading && typeTemplates.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t('reportsModule.noTemplatesForType', {
                  type: t(`reportTypes.${reportType}.simple`),
                })}
              </p>
            )}
          </div>

          {hasChildTemplate(reportType) && (
            <div className="p-field">
              <FieldLabel tooltip={t('reportsModule.childTemplateHelp')}>
                {t('reportsModule.childTemplate')}
              </FieldLabel>
              <SearchSelect
                value={childTemplateId}
                onChange={setChildTemplateId}
                options={childTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
                placeholder={t('reportsModule.noChildTemplate')}
                clearable
              />
            </div>
          )}

          <div className="p-field">
            <FieldLabel hint={usesDates ? dateRangeLabel(range, t) : undefined}>
              {t('reportsModule.reportPeriod')}
            </FieldLabel>
            {!datesRequired && (
              <RadioGroup
                value={specifyDates ? 'dates' : 'flight'}
                onValueChange={(v) => setSpecifyDates(v === 'dates')}
                className="flex flex-col gap-2"
              >
                <Label className="font-normal">
                  <RadioGroupItem value="flight" />
                  {t('reportsModule.useFlight')}
                </Label>
                <Label className="font-normal">
                  <RadioGroupItem value="dates" />
                  {t('reportsModule.specifyDates')}
                </Label>
              </RadioGroup>
            )}
            {usesDates && (
              <DateRangeSelect value={range} onChange={setRange} className="w-full" />
            )}
          </div>

          {availableCurrencies.length > 1 && (
            <div className="p-field">
              <FieldLabel>{t('reports.currency')}</FieldLabel>
              <CurrencySelect value={currency} onValueChange={setCurrency} />
            </div>
          )}

          <div className="p-field">
            <FieldLabel>{t('reports.name')}</FieldLabel>
            <Input
              value={name}
              onChange={(e) => {
                setNameEdited(true)
                setName(e.target.value)
              }}
            />
          </div>

          <div className="p-field">
            <FieldLabel tooltip={t('reports.emailRecipientsHelp')}>
              {t('reports.emailRecipients')}
            </FieldLabel>
            <TokenInput
              value={emails}
              onChange={setEmails}
              lowercase
              placeholder={t('reports.emailPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!valid}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <Play className="size-4" />
            {t('reports.run')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
